import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card, CardBody, Button, Row, Col, Alert } from "reactstrap";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useNavigate } from "react-router-dom";
import { Save, ArrowLeft, RefreshCw, CheckCircle, AlertTriangle, Clock, Calendar, Shield } from "react-feather";

const WEEK_DAYS = [
  { key: "Mon", label: "Mon" },
  { key: "Tue", label: "Tue" },
  { key: "Wed", label: "Wed" },
  { key: "Thu", label: "Thu" },
  { key: "Fri", label: "Fri" },
  { key: "Sat", label: "Sat" },
  { key: "Sun", label: "Sun" },
];

function format12Hour(time24) {
  if (!time24 || typeof time24 !== "string") return "";
  const parts = time24.trim().split(":");
  if (parts.length < 2) return time24;
  const hour = parseInt(parts[0], 10);
  const min = parts[1].padStart(2, "0");
  if (isNaN(hour)) return time24;
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${min} ${ampm}`;
}

function isDayActive(daysStr, dayKey) {
  if (!daysStr) return false;
  const list = daysStr.split(",").map((d) => d.trim().toLowerCase());
  return list.includes(dayKey.toLowerCase());
}

function toggleDay(daysStr, dayKey) {
  const currentList = daysStr
    ? daysStr.split(",").map((d) => d.trim()).filter(Boolean)
    : [];
  const lowerKey = dayKey.toLowerCase();
  const exists = currentList.some((d) => d.toLowerCase() === lowerKey);

  let updated;
  if (exists) {
    updated = currentList.filter((d) => d.toLowerCase() !== lowerKey);
  } else {
    // Preserve standard Mon->Sun order
    const ordered = WEEK_DAYS.map((d) => d.key);
    updated = [...currentList, dayKey].sort(
      (a, b) => ordered.indexOf(a) - ordered.indexOf(b)
    );
  }
  return updated.join(",");
}

function UpdateGlobalOptions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [alertInfo, setAlertInfo] = useState({ show: false, type: "", message: "", details: null });

  const [currentVersions, setCurrentVersions] = useState({ user: 0, captain: 0 });

  const [formData, setFormData] = useState({
    CompanyName: "",
    F_FinancialYearMaster: "1",
    RequestOnPortal: false,
    DriverSearchRadiusKM: "",
    ReachedThresholdMeters: "",
    UserAppVersionCode: "",
    CaptainAppVersionCode: "",
    UserAppPlayStoreUrl: "",
    CaptainAppPlayStoreUrl: "",
    IsServiceOffline: false,
    OfflineNoticeMessage: "",
    Is24x7Operating: true,
    OperatingStartTime: "",
    OperatingEndTime: "",
    OperatingDays: "",
  });

  // Helper to extract first item regardless of API response wrapping
  const extractFirstItem = (json) => {
    if (!json) return null;
    if (Array.isArray(json)) return json[0] || null;
    const candidate = json.dataList || json.DataList || json.data || json.Data || json.result || json.Result;
    if (Array.isArray(candidate)) return candidate[0] || null;
    if (candidate && typeof candidate === "object") {
      const subList = candidate.dataList || candidate.DataList || candidate.data || candidate.Data;
      if (Array.isArray(subList)) return subList[0] || null;
      return candidate;
    }
    if (json.companyName || json.CompanyName || json.userAppVersionCode || json.UserAppVersionCode || json.operatingStartTime || json.OperatingStartTime) {
      return json;
    }
    return null;
  };

  // Fetch initial GlobalOptions and FinancialYearMaster
  const loadData = async () => {
    setLoading(true);
    setAlertInfo({ show: false, type: "", message: "", details: null });
    try {
      // 1. Fetch Financial Years safely
      try {
        const fyRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/FinancialYearMaster/Id/0`);
        if (fyRes.ok) {
          const fyJson = await fyRes.json();
          const fyList = fyJson?.dataList || fyJson?.DataList || fyJson?.data || (Array.isArray(fyJson) ? fyJson : []);
          if (Array.isArray(fyList)) {
            setFinancialYears(fyList);
          }
        }
      } catch (e) {
        console.warn("Could not fetch financial years:", e);
      }

      // 2. Fetch Global Options using standard Master endpoint (like all other masters)
      let item = null;
      try {
        const masterRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/GlobalOption/Id/0`, {
          headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
        });
        if (masterRes.ok) {
          const masterJson = await masterRes.json();
          item = extractFirstItem(masterJson);
        }
      } catch (errMaster) {
        console.warn("Primary Master GlobalOption fetch error:", errMaster);
      }

      // Fallback: If Master endpoint returned nothing or failed, query direct GlobalOption route
      if (!item) {
        try {
          const fallbackRes = await fetch(`${API_WEB_URLS.BASE}GlobalOption/0/token`, {
            headers: { "Cache-Control": "no-cache" },
          });
          if (fallbackRes.ok) {
            const fallbackJson = await fallbackRes.json();
            item = extractFirstItem(fallbackJson);
          }
        } catch (errFallback) {
          console.warn("Fallback GlobalOption fetch error:", errFallback);
        }
      }

      if (item) {
        const userVer = Number(item.UserAppVersionCode ?? item.userAppVersionCode ?? 0);
        const captainVer = Number(item.CaptainAppVersionCode ?? item.captainAppVersionCode ?? 0);
        setCurrentVersions({
          user: userVer,
          captain: captainVer,
        });

        setFormData({
          CompanyName: item.CompanyName ?? item.companyName ?? "",
          F_FinancialYearMaster: String(item.F_FinancialYearMaster ?? item.f_FinancialYearMaster ?? "1"),
          RequestOnPortal: Boolean(item.RequestOnPortal ?? item.requestOnPortal),
          DriverSearchRadiusKM: (item.DriverSearchRadiusKM ?? item.driverSearchRadiusKM) != null ? Number(item.DriverSearchRadiusKM ?? item.driverSearchRadiusKM) : "",
          ReachedThresholdMeters: (item.ReachedThresholdMeters ?? item.reachedThresholdMeters) != null ? Number(item.ReachedThresholdMeters ?? item.reachedThresholdMeters) : "",
          UserAppVersionCode: userVer > 0 ? userVer : "",
          CaptainAppVersionCode: captainVer > 0 ? captainVer : "",
          UserAppPlayStoreUrl: item.UserAppPlayStoreUrl ?? item.userAppPlayStoreUrl ?? "",
          CaptainAppPlayStoreUrl: item.CaptainAppPlayStoreUrl ?? item.captainAppPlayStoreUrl ?? "",
          IsServiceOffline: Boolean(item.IsServiceOffline ?? item.isServiceOffline),
          OfflineNoticeMessage: item.OfflineNoticeMessage ?? item.offlineNoticeMessage ?? "",
          Is24x7Operating: Boolean(item.Is24x7Operating ?? item.is24x7Operating),
          OperatingStartTime: item.OperatingStartTime ?? item.operatingStartTime ?? "",
          OperatingEndTime: item.OperatingEndTime ?? item.operatingEndTime ?? "",
          OperatingDays: item.OperatingDays ?? item.operatingDays ?? "",
        });
      }
    } catch (error) {
      console.error("Error loading GlobalOptions:", error);
      setAlertInfo({ show: true, type: "danger", message: "Failed to load platform settings from server. Please retry.", details: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    CompanyName: Yup.string().trim().required("Company / Firm name is required"),
    DriverSearchRadiusKM: Yup.number()
      .typeError("Driver search radius must be a number")
      .required("Driver search radius is required")
      .min(0.5, "Radius must be at least 0.5 KM")
      .max(100, "Radius cannot exceed 100 KM"),
    ReachedThresholdMeters: Yup.number()
      .typeError("Reached threshold distance must be a number")
      .required("Reached threshold distance is required")
      .min(10, "Reached threshold must be at least 10 meters")
      .max(5000, "Reached threshold cannot exceed 5000 meters"),
    CaptainAppVersionCode: Yup.number()
      .typeError("Captain version code must be a number")
      .required("Captain version code is required")
      .test(
        "min-captain-version",
        function(value) {
          if (currentVersions.captain > 0 && Number(value) < currentVersions.captain) {
            return this.createError({
              message: `Captain App Version Code (${value}) cannot be less than current saved version (${currentVersions.captain}). It must be greater than or equal.`
            });
          }
          return true;
        }
      ),
    UserAppVersionCode: Yup.number()
      .typeError("User version code must be a number")
      .required("User version code is required")
      .test(
        "min-user-version",
        function(value) {
          if (currentVersions.user > 0 && Number(value) < currentVersions.user) {
            return this.createError({
              message: `User App Version Code (${value}) cannot be less than current saved version (${currentVersions.user}). It must be greater than or equal.`
            });
          }
          return true;
        }
      ),
    OfflineNoticeMessage: Yup.string().trim().required("Offline notice message is required"),
    OperatingStartTime: Yup.string().required("Daily opening time is required"),
    OperatingEndTime: Yup.string().required("Daily closing time is required"),
    OperatingDays: Yup.string().required("At least one operating day must be selected"),
  });

  const handleSubmit = async (values) => {
    setSaving(true);
    setAlertInfo({ show: false, type: "", message: "", details: null });
    try {
      let userId = "0";
      let authToken = "";
      try {
        const rawAuth = localStorage.getItem("authUser");
        if (rawAuth && rawAuth !== "undefined" && rawAuth !== "null") {
          const authUser = JSON.parse(rawAuth);
          userId = authUser?.uid || authUser?.Id || authUser?.userId || "0";
          authToken = authUser?.token || "";
        }
      } catch (parseErr) {
        console.warn("Could not parse authUser from localStorage:", parseErr);
      }

      // Pre-validation: Version code cannot be less than current saved version in DB
      if (currentVersions.user > 0 && Number(values.UserAppVersionCode) < currentVersions.user) {
        throw new Error(`User App Version Code (${values.UserAppVersionCode}) cannot be less than current saved version (${currentVersions.user}). It must be greater than or equal.`);
      }
      if (currentVersions.captain > 0 && Number(values.CaptainAppVersionCode) < currentVersions.captain) {
        throw new Error(`Captain App Version Code (${values.CaptainAppVersionCode}) cannot be less than current saved version (${currentVersions.captain}). It must be greater than or equal.`);
      }

      const payload = {
        CompanyName: values.CompanyName ? values.CompanyName.trim() : "",
        F_FinancialYearMaster: Number(values.F_FinancialYearMaster || 1),
        RequestOnPortal: Boolean(values.RequestOnPortal),
        DriverSearchRadiusKM: Number(values.DriverSearchRadiusKM),
        ReachedThresholdMeters: Number(values.ReachedThresholdMeters),
        UserAppVersionCode: Number(values.UserAppVersionCode),
        CaptainAppVersionCode: Number(values.CaptainAppVersionCode),
        UserAppPlayStoreUrl: values.UserAppPlayStoreUrl?.trim() || "",
        CaptainAppPlayStoreUrl: values.CaptainAppPlayStoreUrl?.trim() || "",
        IsServiceOffline: Boolean(values.IsServiceOffline),
        OfflineNoticeMessage: values.OfflineNoticeMessage ? values.OfflineNoticeMessage.trim() : "",
        Is24x7Operating: Boolean(values.Is24x7Operating),
        OperatingStartTime: values.OperatingStartTime?.trim() || "",
        OperatingEndTime: values.OperatingEndTime?.trim() || "",
        OperatingDays: values.OperatingDays?.trim() || "",
      };

      // Always use safe route with literal "token" parameter so IIS/ASP.NET never rejects with 400 Bad Request
      const targetUrl = `${API_WEB_URLS.BASE}GlobalOption/${userId}/token`;

      const reqHeaders = {
        "Content-Type": "application/json",
        "Accept": "application/json",
      };
      if (authToken) {
        reqHeaders["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await fetch(targetUrl, {
        method: "POST",
        headers: reqHeaders,
        body: JSON.stringify(payload),
      });

      let resJson = null;
      try {
        resJson = await response.json();
      } catch (e) {
        console.warn("Response body parse warning:", e);
      }

      if (response.ok && resJson?.Success !== false && resJson?.success !== false) {
        setCurrentVersions({
          user: Number(payload.UserAppVersionCode),
          captain: Number(payload.CaptainAppVersionCode),
        });

        setAlertInfo({
          show: true,
          type: "success",
          message: "Global Options & Firm Settings updated successfully!",
          details: {
            firm: payload.CompanyName,
            timing: payload.Is24x7Operating
              ? "24x7 Round-the-Clock (Always Open)"
              : `${format12Hour(payload.OperatingStartTime)} to ${format12Hour(payload.OperatingEndTime)}`,
            days: payload.OperatingDays,
            offline: payload.IsServiceOffline ? "OFFLINE (Emergency Mode Active)" : "ONLINE (Normal Operation)",
          },
        });
        setFormData(values);
      } else {
        throw new Error(resJson?.Message || resJson?.message || `Server responded with status ${response.status}`);
      }
    } catch (err) {
      console.error("Save error:", err);
      setAlertInfo({
        show: true,
        type: "danger",
        message: `Failed to save settings: ${err.message || "Unknown error occurred"}`,
        details: null,
      });
    } finally {
      setSaving(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading settings...</span>
        </div>
        <div className="text-muted fw-semibold">Loading platform settings...</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3" style={{ maxWidth: 1100 }}>
      {/* Header Bar */}
      <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
        <div>
          <h5 className="mb-0 text-dark fw-bold d-flex align-items-center">
            <i className="bx bx-slider-alt me-2 text-primary"></i>
            Global Options & Platform Settings
          </h5>
          <small className="text-muted">
            Configure Firm/Company Name, 24x7 / Custom Working Hours, Active Operating Days, and Live App Parameters.
          </small>
        </div>
        <div className="d-flex gap-2">
          <Button
            type="button"
            size="sm"
            color="light"
            onClick={loadData}
            disabled={saving}
            className="d-flex align-items-center"
          >
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
          <Button
            type="button"
            size="sm"
            color="secondary"
            onClick={() => navigate(-1)}
            className="d-flex align-items-center"
          >
            <ArrowLeft size={14} className="me-1" /> Back
          </Button>
          <Button
            type="submit"
            form="global-options-form"
            size="sm"
            color="primary"
            disabled={saving}
            className="d-flex align-items-center px-3 fw-bold shadow-sm"
          >
            <Save size={14} className="me-1" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Success / Error Banner */}
      {alertInfo.show && (
        <Alert color={alertInfo.type} className="shadow-sm mb-3">
          <div className="d-flex align-items-start">
            {alertInfo.type === "success" ? (
              <CheckCircle size={20} className="me-2 text-success mt-1 flex-shrink-0" />
            ) : (
              <AlertTriangle size={20} className="me-2 text-danger mt-1 flex-shrink-0" />
            )}
            <div>
              <strong className="d-block font-size-14">{alertInfo.message}</strong>
              {alertInfo.details && (
                <div className="mt-2 pt-2 border-top border-success-subtle font-size-12">
                  <div>🏢 <b>Firm Name:</b> {alertInfo.details.firm}</div>
                  <div>⏰ <b>Working Hours:</b> {alertInfo.details.timing}</div>
                  <div>📅 <b>Active Days:</b> {alertInfo.details.days}</div>
                  <div>📡 <b>Service Status:</b> {alertInfo.details.offline}</div>
                </div>
              )}
            </div>
          </div>
        </Alert>
      )}

      <Formik
        initialValues={formData}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, submitCount }) => (
          <Form id="global-options-form">
            {/* Show any validation errors at top if submission was attempted */}
            {submitCount > 0 && Object.keys(errors).length > 0 && (
              <Alert color="danger" className="shadow-sm mb-3">
                <div className="d-flex align-items-center mb-1">
                  <AlertTriangle size={18} className="me-2 text-danger" />
                  <strong>Please correct the following fields before saving:</strong>
                </div>
                <ul className="mb-0 ps-3 font-size-12">
                  {Object.entries(errors).map(([field, msg]) => (
                    <li key={field}><b>{field}</b>: {msg}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <Row className="g-3">
              {/* 1. 🏢 Company / Firm Profile & Financial Year */}
              <Col lg={12}>
                <Card className="border shadow-sm mb-3" style={{ borderLeft: "5px solid #0284C7" }}>
                  <div className="card-header bg-soft-info py-2 border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 text-dark fw-bold">
                      <i className="bx bx-building me-2 text-info"></i>
                      1. Company / Firm Profile & Financial Year
                    </h6>
                    <span className="badge bg-info-subtle text-info border border-info">
                      Firm & Master Setup
                    </span>
                  </div>
                  <CardBody className="p-3">
                    <Row className="g-3">
                      <Col md={7}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          Company / Firm Name <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="text"
                          name="CompanyName"
                          className={`form-control ${errors.CompanyName && touched.CompanyName ? "is-invalid" : ""}`}
                          placeholder="KirayaGo Logistics"
                        />
                        <small className="text-muted d-block mt-1">
                          Platform receipts, trip invoices, customer alerts aur driver vouchers par ye firm name reflect hoga.
                        </small>
                        <ErrorMessage name="CompanyName" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      <Col md={5}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          Active Financial Year <span className="text-danger">*</span>
                        </label>
                        <Field as="select" name="F_FinancialYearMaster" className="form-select">
                          <option value="1">FY 2026-27 (Default)</option>
                          {financialYears.map((fy) => (
                            <option key={fy.Id} value={fy.Id}>{fy.Name || `FY #${fy.Id}`}</option>
                          ))}
                        </Field>
                        <small className="text-muted d-block mt-1">
                          Transactions & vouchers ke liye active financial year.
                        </small>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* 2. ⏰ Working Hours & Offline Service Notice (Customer & Driver Apps) */}
              <Col lg={12}>
                <Card className="border shadow-sm mb-3" style={{ borderLeft: "5px solid #EA580C" }}>
                  <div className="card-header bg-soft-warning py-2 border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 text-dark fw-bold">
                      <i className="bx bx-time-five me-2 text-warning"></i>
                      2. Working Hours, Operating Days & Offline Notice (Live Apps)
                    </h6>
                    <span className="badge bg-warning-subtle text-warning-emphasis border border-warning">
                      Live App Enforcement
                    </span>
                  </div>
                  <CardBody className="p-3">
                    <Row className="g-3">
                      {/* 24x7 Operation Toggle */}
                      <Col md={6}>
                        <div 
                          className={`p-3 rounded border h-100 transition-all ${
                            values.Is24x7Operating 
                              ? "bg-soft-primary border-primary shadow-sm" 
                              : "bg-light border-secondary"
                          }`}
                          style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                          onClick={() => setFieldValue("Is24x7Operating", !values.Is24x7Operating)}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <label 
                              htmlFor="is24x7OperatingSwitch"
                              className="form-label fw-bold text-dark mb-0 d-flex align-items-center"
                              style={{ cursor: "pointer" }}
                            >
                              <Clock size={16} className="me-2 text-warning" />
                              24x7 Round-the-Clock Service
                            </label>
                            <div className="form-check form-switch form-switch-md mb-0">
                              <input
                                type="checkbox"
                                name="Is24x7Operating"
                                className="form-check-input"
                                id="is24x7OperatingSwitch"
                                checked={Boolean(values.Is24x7Operating)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setFieldValue("Is24x7Operating", e.target.checked);
                                }}
                                style={{ cursor: "pointer", width: "2.4em", height: "1.3em" }}
                              />
                            </div>
                          </div>
                          <div className="d-flex align-items-center justify-content-between">
                            <small className="text-muted">
                              Agar <b>ON</b> hai to service din-raat 24 ghante active rahegi.
                            </small>
                            <span className={`badge ${values.Is24x7Operating ? "bg-primary" : "bg-secondary"} ms-2`}>
                              {values.Is24x7Operating ? "Active 24x7" : "Custom Hours"}
                            </span>
                          </div>
                        </div>
                      </Col>

                      {/* Emergency Service Offline Switch */}
                      <Col md={6}>
                        <div 
                          className={`p-3 rounded border h-100 transition-all ${
                            values.IsServiceOffline 
                              ? "bg-soft-danger border-danger shadow-sm" 
                              : "bg-light border-secondary"
                          }`}
                          style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                          onClick={() => setFieldValue("IsServiceOffline", !values.IsServiceOffline)}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <label 
                              htmlFor="isServiceOfflineSwitch"
                              className="form-label fw-bold text-danger mb-0 d-flex align-items-center"
                              style={{ cursor: "pointer" }}
                            >
                              <Shield size={16} className="me-2 text-danger" />
                              Emergency Offline / Maintenance Mode
                            </label>
                            <div className="form-check form-switch form-switch-md mb-0">
                              <input
                                type="checkbox"
                                name="IsServiceOffline"
                                className="form-check-input"
                                id="isServiceOfflineSwitch"
                                checked={Boolean(values.IsServiceOffline)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setFieldValue("IsServiceOffline", e.target.checked);
                                }}
                                style={{ cursor: "pointer", width: "2.4em", height: "1.3em" }}
                              />
                            </div>
                          </div>
                          <div className="d-flex align-items-center justify-content-between">
                            <small className="text-danger">
                              Emergency me isko <b>ON</b> karne se booking turant pause ho jayegi.
                            </small>
                            <span className={`badge ${values.IsServiceOffline ? "bg-danger" : "bg-success"} ms-2`}>
                              {values.IsServiceOffline ? "OFFLINE ACTIVE" : "ONLINE NORMAL"}
                            </span>
                          </div>
                        </div>
                      </Col>

                      {/* Operational Start & End Times */}
                      <Col md={3}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          <i className="bx bx-alarm me-1 text-success"></i>
                          Daily Opening Time <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="time"
                          name="OperatingStartTime"
                          className="form-control"
                        />
                        <div className="mt-1 d-flex align-items-center justify-content-between">
                          <small className="text-muted font-size-11">24-Hour Format</small>
                          <span className="badge bg-success-subtle text-success border border-success font-size-11">
                            {format12Hour(values.OperatingStartTime) || "06:00 AM"}
                          </span>
                        </div>
                        <ErrorMessage name="OperatingStartTime" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      <Col md={3}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          <i className="bx bx-moon me-1 text-info"></i>
                          Daily Closing Time <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="time"
                          name="OperatingEndTime"
                          className="form-control"
                        />
                        <div className="mt-1 d-flex align-items-center justify-content-between">
                          <small className="text-muted font-size-11">24-Hour Format</small>
                          <span className="badge bg-info-subtle text-info border border-info font-size-11">
                            {format12Hour(values.OperatingEndTime) || "11:00 PM"}
                          </span>
                        </div>
                        <ErrorMessage name="OperatingEndTime" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      {/* Active Operating Days with Badges & Presets */}
                      <Col md={6}>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <label className="form-label fw-semibold text-dark mb-0">
                            <Calendar size={14} className="me-1 text-primary" />
                            Active Operating Days <span className="text-danger">*</span>
                          </label>
                          <div className="d-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary py-0 px-1 font-size-11"
                              onClick={() => setFieldValue("OperatingDays", "Mon,Tue,Wed,Thu,Fri,Sat,Sun")}
                            >
                              All 7 Days
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary py-0 px-1 font-size-11"
                              onClick={() => setFieldValue("OperatingDays", "Mon,Tue,Wed,Thu,Fri,Sat")}
                            >
                              Mon-Sat
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary py-0 px-1 font-size-11"
                              onClick={() => setFieldValue("OperatingDays", "Mon,Tue,Wed,Thu,Fri")}
                            >
                              Mon-Fri
                            </button>
                          </div>
                        </div>

                        {/* Interactive Pill Buttons for Each Day */}
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {WEEK_DAYS.map((day) => {
                            const isSelected = isDayActive(values.OperatingDays, day.key);
                            return (
                              <button
                                key={day.key}
                                type="button"
                                className={`btn btn-sm py-1 px-2.5 fw-semibold ${
                                  isSelected ? "btn-primary shadow-sm" : "btn-outline-secondary"
                                }`}
                                style={{ fontSize: 12, borderRadius: 6 }}
                                onClick={() => {
                                  const newDays = toggleDay(values.OperatingDays, day.key);
                                  setFieldValue("OperatingDays", newDays);
                                }}
                              >
                                {isSelected ? `✓ ${day.label}` : day.label}
                              </button>
                            );
                          })}
                        </div>

                        <Field
                          type="text"
                          name="OperatingDays"
                          className={`form-control form-control-sm ${errors.OperatingDays && touched.OperatingDays ? "is-invalid" : ""}`}
                          placeholder="Mon,Tue,Wed,Thu,Fri,Sat,Sun"
                        />
                        <ErrorMessage name="OperatingDays" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      {/* Custom Notice Message */}
                      <Col md={12}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          <i className="bx bx-message-alt-error me-1 text-danger"></i>
                          Customer & Driver Offline Notice Message <span className="text-danger">*</span>
                        </label>
                        <Field
                          as="textarea"
                          rows={2}
                          name="OfflineNoticeMessage"
                          className={`form-control ${errors.OfflineNoticeMessage && touched.OfflineNoticeMessage ? "is-invalid" : ""}`}
                          placeholder="Currently we are not available, service will resume shortly."
                        />
                        <ErrorMessage name="OfflineNoticeMessage" component="div" className="text-danger font-size-12 mt-1" />
                        
                        {/* Live Mobile Preview */}
                        <div className="mt-2 p-2.5 bg-light rounded border d-flex align-items-center gap-2">
                          <span className="badge bg-dark">Live Mobile Preview</span>
                          <span className="text-muted font-size-13 fst-italic">
                            "🌙 {values?.OfflineNoticeMessage || "Currently we are not available, service will resume shortly."}"
                          </span>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* 3. 🚖 Driver Dispatch & Distance Range Settings */}
              <Col lg={12}>
                <Card className="border shadow-sm mb-3" style={{ borderLeft: "5px solid #2563EB" }}>
                  <div className="card-header bg-soft-primary py-2 border-bottom">
                    <h6 className="mb-0 text-primary fw-bold">
                      <i className="bx bx-navigation me-2"></i>
                      3. Driver Dispatch & Distance Range Settings (Live App)
                    </h6>
                  </div>
                  <CardBody className="p-3">
                    <Row className="g-3">
                      {/* Driver Search Radius KM */}
                      <Col md={6}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          <i className="bx bx-radar me-1 text-primary"></i>
                          Driver Search / Nearby Alert Radius (in KM) <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <Field
                            type="number"
                            step="0.1"
                            name="DriverSearchRadiusKM"
                            className={`form-control ${errors.DriverSearchRadiusKM && touched.DriverSearchRadiusKM ? "is-invalid" : ""}`}
                            placeholder="e.g. 5.0"
                          />
                          <span className="input-group-text bg-light fw-bold">KM</span>
                        </div>
                        <small className="text-muted d-block mt-1">
                          Pickup location ke is radius (KM) ke daayre mein online captains ko booking alert jayega.
                        </small>
                        <ErrorMessage name="DriverSearchRadiusKM" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      {/* Reached Threshold Distance Meters */}
                      <Col md={6}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          <i className="bx bx-map-pin me-1 text-success"></i>
                          Pickup & Drop "Reached" Threshold (in Meters) <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <Field
                            type="number"
                            step="1"
                            name="ReachedThresholdMeters"
                            className={`form-control ${errors.ReachedThresholdMeters && touched.ReachedThresholdMeters ? "is-invalid" : ""}`}
                            placeholder="e.g. 100"
                          />
                          <span className="input-group-text bg-light fw-bold">Meters</span>
                        </div>
                        <small className="text-muted d-block mt-1">
                          Captain jab pickup/drop location ke itne meters ke daayre mein pahunchega tab "Reached" activate hoga.
                        </small>
                        <ErrorMessage name="ReachedThresholdMeters" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* 4. 📱 Mobile App Version Management */}
              <Col lg={12}>
                <Card className="border shadow-sm mb-3" style={{ borderLeft: "5px solid #059669" }}>
                  <div className="card-header bg-soft-success py-2 border-bottom">
                    <h6 className="mb-0 text-success fw-bold">
                      <i className="bx bx-mobile-alt me-2"></i>
                      4. Mobile App Version Control & Play Store Links
                    </h6>
                  </div>
                  <CardBody className="p-3">
                    <Row className="g-3">
                      {/* Captain App Version Code */}
                      <Col md={3}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label fw-semibold text-dark mb-0">
                            Captain App Min Version Code <span className="text-danger">*</span>
                          </label>
                          {currentVersions.captain > 0 && (
                            <span className="badge bg-soft-info text-info border">
                              Current: {currentVersions.captain}
                            </span>
                          )}
                        </div>
                        <Field
                          type="number"
                          name="CaptainAppVersionCode"
                          min={currentVersions.captain || 1}
                          className={`form-control ${errors.CaptainAppVersionCode && touched.CaptainAppVersionCode ? "is-invalid" : ""}`}
                          placeholder={currentVersions.captain ? `≥ ${currentVersions.captain}` : "Version Code"}
                        />
                        <small className="text-muted d-block mt-1 font-size-11">
                          {currentVersions.captain > 0 ? `Must be ≥ ${currentVersions.captain}` : "Enter version code"}
                        </small>
                        <ErrorMessage name="CaptainAppVersionCode" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      {/* Captain App PlayStore URL */}
                      <Col md={9}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          Captain App Google Play Store URL
                        </label>
                        <Field
                          type="text"
                          name="CaptainAppPlayStoreUrl"
                          className="form-control"
                          placeholder="https://play.google.com/store/apps/details?id=com.KirayaGoCaptain"
                        />
                      </Col>

                      {/* User App Version Code */}
                      <Col md={3}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label fw-semibold text-dark mb-0">
                            User App Min Version Code <span className="text-danger">*</span>
                          </label>
                          {currentVersions.user > 0 && (
                            <span className="badge bg-soft-info text-info border">
                              Current: {currentVersions.user}
                            </span>
                          )}
                        </div>
                        <Field
                          type="number"
                          name="UserAppVersionCode"
                          min={currentVersions.user || 1}
                          className={`form-control ${errors.UserAppVersionCode && touched.UserAppVersionCode ? "is-invalid" : ""}`}
                          placeholder={currentVersions.user ? `≥ ${currentVersions.user}` : "Version Code"}
                        />
                        <small className="text-muted d-block mt-1 font-size-11">
                          {currentVersions.user > 0 ? `Must be ≥ ${currentVersions.user}` : "Enter version code"}
                        </small>
                        <ErrorMessage name="UserAppVersionCode" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      {/* User App PlayStore URL */}
                      <Col md={9}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          User App Google Play Store URL
                        </label>
                        <Field
                          type="text"
                          name="UserAppPlayStoreUrl"
                          className="form-control"
                          placeholder="https://play.google.com/store/apps/details?id=com.KirayagoNew"
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Bottom Action Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-2 mb-4">
              <Button type="button" color="light" onClick={() => navigate(-1)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={saving} className="px-4 fw-bold shadow-sm">
                <Save size={15} className="me-1" />
                {saving ? "Saving Changes..." : "Save Platform Settings"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default UpdateGlobalOptions;
